// cjs-interop.js: CommonJS 包在 Vite 8 + Rolldown ESM 严格模式下的互操作辅助工具
//
// 背景:
//   jszip/qrcode 等包是 CommonJS 格式
//   - jszip: module.exports = class (CommonJS)
//   - qrcode: module.exports = { toCanvas, toString, ... } (CommonJS 纯对象)
//   它们都没有 ESM default 导出。Vite 8 + Rolldown 严格 ESM 模式下:
//     - `import X from 'pkg'`  可能返回 undefined 或 Module Namespace 对象
//     - `import * as NS from 'pkg'`  返回 Module Namespace 对象(不可调用)
//
// 解决方案:
//   用 `import * as NS from 'pkg'` 拿到命名空间,然后用 resolveCJS 提取真实导出
//   关键:不能用 Object.keys 找第一个 function,因为 qrcode 这种纯对象导出
//   会错误返回 qrcode.create 函数,而非整个 qrcode 对象

/**
 * 判断对象是否是 Vite 8 互操作产生的 Module Namespace Object
 * Module Namespace 对象特征:
 *   - typeof === 'object' (即使原始导出是 function/class)
 *   - 有 Symbol.toStringTag === 'Module'
 *   - 不可枚举的属性
 */
function isModuleNamespace(ns) {
  return (
    typeof ns === 'object' &&
    ns !== null &&
    (
      ns[Symbol.toStringTag] === 'Module' ||
      // Vite 预构建后的命名空间对象特征
      (ns.default !== undefined || Object.keys(ns).length > 0)
    )
  );
}

/**
 * 从 Vite 8 ESM 互操作后的命名空间对象中提取真实的 CommonJS 导出
 * @param {Object} ns - `import * as ns from 'pkg'` 得到的命名空间对象
 * @param {string} [pkgName] - 包名(用于调试日志)
 * @returns {Function|Class|Object} 真实的 CommonJS 导出
 */
export function resolveCJS(ns, pkgName) {
  if (!ns) {
    console.warn(`[resolveCJS] ${pkgName || 'unknown'}: ns is null/undefined`);
    return ns;
  }

  // 情况 1: ns 本身就是 function/class
  // Vite 在某些情况下会保留原始 CJS 导出,`import * as NS` 的 NS 可直接调用
  if (typeof ns === 'function') {
    console.log(`[resolveCJS] ${pkgName || 'unknown'}: ns is function/class itself`);
    return ns;
  }

  // 情况 2: ns.default 存在且是 function/class (Vite 预构建后的标准形式)
  // html2canvas 和 jszip 走这条路径
  if (ns.default && typeof ns.default === 'function') {
    console.log(`[resolveCJS] ${pkgName || 'unknown'}: using ns.default (function)`);
    return ns.default;
  }

  // 情况 3: ns.default 存在且是对象,检查是否是纯对象导出(如 qrcode)
  // qrcode 包导出的是 {toCanvas, toString, ...},default 应该就是这个对象
  if (ns.default && typeof ns.default === 'object' && ns.default !== null) {
    // 检查是否是 class 的实例(有 prototype.constructor)
    if (ns.default.prototype && typeof ns.default.prototype.constructor === 'function') {
      console.log(`[resolveCJS] ${pkgName || 'unknown'}: using ns.default (class via prototype)`);
      return ns.default;
    }
    // 检查是否有 toCanvas 等方法(针对 qrcode)
    if (typeof ns.default.toCanvas === 'function') {
      console.log(`[resolveCJS] ${pkgName || 'unknown'}: using ns.default (object with methods)`);
      return ns.default;
    }
  }

  // 情况 4: ns 是对象,检查是否有 prototype(说明是 class 本身)
  // jszip 在某些 Vite 版本下走这条路径
  if (typeof ns === 'object' && ns.prototype && typeof ns.prototype.constructor === 'function') {
    console.log(`[resolveCJS] ${pkgName || 'unknown'}: using ns itself (class with prototype)`);
    return ns;
  }

  // 情况 5: ns 是纯对象,检查是否有 toCanvas 等方法(针对 qrcode 无 default 时)
  // 注意:不再遍历 Object.keys 找第一个 function,避免误返回 qrcode.create
  if (typeof ns === 'object' && typeof ns.toCanvas === 'function') {
    console.log(`[resolveCJS] ${pkgName || 'unknown'}: using ns itself (object with toCanvas)`);
    return ns;
  }

  // 情况 6: 最后 fallback - 返回 ns 本身
  console.warn(`[resolveCJS] ${pkgName || 'unknown'}: fallback to ns itself, keys:`, Object.keys(ns));
  return ns;
}
